class BulkUpload extends AppBase {
  constructor() {
    super();

    this.dropZone = document.querySelector('#bulk-upload__dropzone');
    this.dropZoneActions = document.querySelector('.file__dropzone-actions');
    this.dropZoneContainer = document.querySelector('.file__dropzone-container');
    this.dropZoneTableData = document.querySelector('.file__dropzone-data');
    this.bulkFileInput = document.querySelector('#bulk-file-input');

    this.worksheetMappedData = [];
    this.worksheetHeaders = [];
    this.worksheetErrorRows = [];

    this.init();
  }

  init() {
    this.addEventListeners();
  }

  addEventListeners() {
    this.dropZone.addEventListener('dragover', (e) => {
      e.target.classList.add('active');
      e.stopPropagation();
      e.preventDefault();
    });

    this.dropZone.addEventListener('dragleave', (e) => {
      e.target.classList.remove('active');
    });

    this.dropZone.addEventListener('drop', this.dropFile.bind(this));

    document.querySelector('[data-type="import"]').addEventListener('click', (e) => {
      this.importItems(e);
    });

    document.querySelector('[data-type="clear"]').addEventListener('click', (e) => {
      window.location.href = getPageUrl('cart');
    });

    document.querySelector('#bulk-upload__dropzone').addEventListener('click', (event) => {
      this.bulkFileInput?.click();
    });

    this.bulkFileInput?.addEventListener('change', this.dropFile.bind(this));

    document.querySelector('.download-error-file').addEventListener('click', () => {
      this.downloadErrorFile();

      return false;
    });
  }

  toggleLoadingOverlay(style = 'hide') {
    window.appLoader.loadingOverlay.style.display = style === 'show' ? 'flex' : 'none';
  }

  async fetchData(endpoint, options) {
    const response = await fetch(endpoint, options);

    if (!response.ok) {
      return response.text().then(text => {
        throw new Error(text)
      });
    }

    const data = await response.json();

    return data;
  }

  dropFile(e) {
    e.stopPropagation();
    e.preventDefault();
    e.target.classList.remove('active');

    const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    const allowedFileTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    if (files.length > 1) {
      this.alertMessages(
        '#bulk-upload .alert-messages',
        'error',
        'Please drag and drop single file. Multiple files not allowed.',
        true
      );

      return false;
    }

    const file = files[0];

    if (!allowedFileTypes.includes(file.type)) {
      this.alertMessages(
        '#bulk-upload .alert-messages',
        'error',
        'Invalid file type. Please upload csv or excel file.',
        true
      );

      return false;
    }

    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    reader.onload = async () => {
      document.querySelector('#bulk-upload .alert-messages').classList.remove('alert-show');
      this.toggleLoadingOverlay('show');

      const arrayBuffer = new Uint8Array(reader.result),
        binaryString = String.fromCharCode.apply(null, arrayBuffer),
        headers = ['sku', 'quantity'];

      const workbook = XLSX.read(binaryString, {
        type: 'binary',
      });

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      let worksheetHeaders = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0];
      worksheetHeaders = worksheetHeaders.map((field) => (typeof field === 'number' ? field : field.toLowerCase()));

      this.worksheetHeaders = worksheetHeaders;

      const validate = headers.every((header) => worksheetHeaders.includes(header));

      if (!validate) {
        this.alertMessages(
          '#bulk-upload .alert-messages',
          'error',
          'The headers with SKU and quantity columns are missing in the given file. Please upload the file as per the sample file.',
          true
        );

        this.toggleLoadingOverlay('hide');

        return false;
      }

      this.worksheetMappedData = XLSX.utils.sheet_to_json(worksheet, { raw: true }).map(obj =>
        Object.fromEntries(Object.entries(obj).map(([key, value]) => [key.toLowerCase(), value]))
      );

      if (this.worksheetMappedData.length > 250) {
        this.alertMessages(
          '#bulk-upload .alert-messages',
          'error',
          'The file should not contain more than 250 SKUs.',
          true
        );

        this.toggleLoadingOverlay('hide');

        return false;
      }

      await this.renderWorksheet(XLSX.utils.sheet_to_json(worksheet, { header: 1 }));
    };
  }

  async renderWorksheet(data) {
    this.dropZone.classList.add('hide');

    const errorRows = [];
    const table = document.createElement('table');
    table.classList.add('excelsheet__table');

    const skus = [];
    const rowsHtml = data.map((row, index) => {
      const isHeader = index === 0;
      const tag = isHeader ? 'th' : 'td';
      const sku = !isHeader ? this.worksheetMappedData[index - 1]?.sku?.toString() || '' : '';
      const quantity = !isHeader ? this.worksheetMappedData[index - 1]?.quantity || 0 : 0;

      if (sku) {
        skus.push(sku);
      }

      const checkboxCell = isHeader
        ? `<${tag} width="60px" class="text-center"><input type="checkbox" checked="checked" id="select-all-items" /></${tag}>`
        : `<${tag} width="60px" class="text-center"><input type="checkbox" checked="checked" /></${tag}>`;

      const dataCells = row.map(cell => `<${tag}>${cell}</${tag}>`).join('');
      const statusCell = isHeader
        ? `<th class="th-errors">Errors</th>`
        : `<td class="td-errors"></td>`;

      return `<tr data-sku="${sku}" data-quantity="${quantity}" data-index="row-${index}">${checkboxCell}${dataCells}${statusCell}</tr>`;
    });

    table.innerHTML = rowsHtml.join('');

    this.dropZoneActions.classList.remove('visibility-hidden');
    this.dropZoneContainer.classList.add('hide');
    this.dropZoneTableData.innerHTML = '';
    this.dropZoneTableData.appendChild(table);

    const promises = skus.map(async (sku, index) => {
      const products = await this.fetchData(`${window.Shopify.routes.root}search/?view=rest&q=variants.sku:${sku}`);
      const trElement = document.querySelector(`tr[data-sku="${sku}"][data-index="row-${index + 1}"]`);

      if (products.length > 0) {
        for (const product of products) {
          const filteredVariant = product.variants.find(variant => variant.sku.toLowerCase() === sku.toLowerCase());

          if (filteredVariant) {
            const kitItems = await this.getAssociationItems(sku);
            if (kitItems.length > 0) {
              await this.renderAssosications(trElement, kitItems);

              trElement.remove();
            } else {
              trElement.setAttribute('data-variant-id', filteredVariant.id);
            }
          } else {
            trElement.querySelector('.td-errors').innerHTML = 'Product does not exists';
            trElement.classList.add('sku-not-found');
            trElement.querySelector('input').checked = false;
            trElement.querySelector('input').setAttribute('disabled', 'disabled');

            errorRows.push({
              sku: sku,
              quantity: trElement.getAttribute('data-quantity'),
              reason: 'Product does not exists'
            });
          }
        }
      } else {
        trElement.querySelector('.td-errors').innerHTML = 'Product does not exists';
        trElement.classList.add('sku-not-found');
        trElement.querySelector('input').checked = false;
        trElement.querySelector('input').setAttribute('disabled', 'disabled');

        errorRows.push({
          sku: sku,
          quantity: trElement.getAttribute('data-quantity'),
          reason: 'Product does not exists'
        });

      }
    });

    await Promise.all(promises);

    document.querySelector('[data-step="step1"]').classList.remove('active');
    document.querySelector('[data-step="step2"]').classList.add('active');

    const selectAllCheckbox = table.querySelector('#select-all-items');
    if (selectAllCheckbox) {
      selectAllCheckbox.addEventListener('change', () => {
        const checkboxes = table.querySelectorAll('tbody td input[type="checkbox"]:not(:disabled)');
        checkboxes.forEach(checkbox => {
          checkbox.checked = selectAllCheckbox.checked;
        });
      });

      table.addEventListener('change', (event) => {
        if (event.target.matches('tbody input[type="checkbox"]')) {
          const checkboxes = table.querySelectorAll('tbody td input[type="checkbox"]:not(:disabled)');
          const selectedCheckboxes = table.querySelectorAll('tbody td input[type="checkbox"]:checked:not(:disabled)');

          selectAllCheckbox.checked = checkboxes.length === selectedCheckboxes.length;

          return;
        }
      });

      table.addEventListener('click', (event) => {
        if (!event.target.matches('tbody input[type="checkbox"]')) {
          const trElement = event.target.closest('tr:not(.sku-not-found)');

          if (trElement) {
            const checkbox = trElement.querySelector('input[type="checkbox"]');
            checkbox.checked = !checkbox.checked;

            const table = event.target.closest('.excelsheet__table');
            const checkboxes = table.querySelectorAll('tbody td input[type="checkbox"]:not(:disabled)');
            const selectedCheckboxes = table.querySelectorAll('tbody td input[type="checkbox"]:checked:not(:disabled)');

            table.querySelector('#select-all-items').checked = checkboxes.length === selectedCheckboxes.length;
          }
        }
      });
    }

    if (errorRows.length) {
      this.worksheetErrorRows = errorRows;

      this.alertMessages(
        '#bulk-upload .alert-messages',
        'error',
        'Item(s) not available or do not exist',
        false
      );

      document.querySelectorAll('.download-sample-file').forEach((el) => el.classList.add('hide'));
      document.querySelector('.download-error-file').classList.remove('hide');
    }

    this.toggleLoadingOverlay('hide');
  }

  async importItems() {
    const checkedCheckboxes = document.querySelectorAll('.excelsheet__table td input[type="checkbox"]:checked');
    if (checkedCheckboxes.length === 0) {
      this.alertMessages(
        '#bulk-upload .alert-messages',
        'error',
        'Please select the items to be imported',
        true
      );

      return;
    }

    this.toggleLoadingOverlay('show');
    const items = Array.from(checkedCheckboxes, checkbox => ({
      id: checkbox.closest('tr').dataset.variantId,
      quantity: checkbox.closest('tr').dataset.quantity
    }));

    await this.addCartItems(items);
    this.toggleLoadingOverlay('hide');

    window.location.href = getPageUrl('cart');
  }

  async addCartItems(items = []) {
    if (items.length === 0) {
      return false;
    }

    let body = {
      items: items
    };

    try {
      await this.fetchData(
        window.Shopify.routes.root + 'cart/add.js',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        }
      );

      return true;
    } catch (error) {
      return false;
    }
  }

  async getAssociationItems(variantSku) {
    if (!variantSku) {
      return [];
    }

    try {
      const apiResponse = await this.fetchData(
        `https://bdsgallery-function.azurewebsites.net/api/associations?child=true&sku=${encodeURIComponent(variantSku)}`
      );

      if (!apiResponse || apiResponse.length === 0 || !apiResponse[0].data) {
        return [];
      }

      const parentSku = apiResponse[0].sku;

      if (!parentSku || parentSku.toLowerCase() !== variantSku.toLowerCase()) {
        return [];
      }

      const items = apiResponse[0].data;

      if (items.length === 0) {
        return [];
      }

      const skus = items.map(item => item.sku);
      const variantsMap = await this.getKitItemsBatch(skus);

      const kitItems = items
        .map(item => {
          const filteredVariant = variantsMap.get(item.sku.toLowerCase());

          if (filteredVariant) {
            return {
              quantity: item.qty,
              ...filteredVariant,
              title: item.title
            };
          }
          return null;
        })
        .filter(item => item !== null);

      return kitItems;
    } catch (error) {
      console.error('Error fetching associations:', error);
      return [];
    }
  }

  async getKitItemsBatch(skus) {
    if (!skus || skus.length === 0) {
      return new Map();
    }

    const query = skus.map(sku => `variants.sku:${sku}`).join(' OR ');
    const products = await this.fetchData(`${window.Shopify.routes.root}search/?view=rest&q=${encodeURIComponent(query)}`);

    const variantsMap = new Map();

    if (products && products.length > 0) {
      for (const product of products) {
        for (const variant of product.variants) {
          const skuLower = variant.sku.toLowerCase();

          if (skus.some(sku => sku.toLowerCase() === skuLower)) {
            variantsMap.set(skuLower, {
              ...variant,
              featured_image: variant.featured_image ?? product.featured_image
            });
          }
        }
      }
    }

    return variantsMap;
  }

  async renderAssosications(appendTo, items) {
    items.forEach(item => {
      const quantity = parseInt(item.quantity) * parseInt(appendTo.getAttribute('data-quantity'));

      appendTo.insertAdjacentHTML('beforebegin',
        `<tr data-sku="${item.sku}" data-quantity="${quantity}" data-index="${appendTo.getAttribute('data-index')}-${item.id}" data-variant-id="${item.id}">
          <td width="60px" class="text-center"><input type="checkbox" checked="checked" /></td>
          <td>${item.sku}</td>
          <td>${quantity}</td>
          <td class="td-errors"></td>
        </tr>`
      );
    });
  }

  downloadErrorFile() {
    const fileHeaders = [...this.worksheetHeaders, 'reason'];
    const csvContent = this.worksheetErrorRows.map(rejectedItem => {
      return fileHeaders.map(header => rejectedItem[header.toLowerCase()] || '').join(',');
    }).join("\r\n");

    this.createBlobAndDownload(`${fileHeaders.join(',')}\r\n${csvContent}`, `error-items-${Date.now()}.csv`);
  }

  createBlobAndDownload(content, fileName) {
    let blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    let url = URL.createObjectURL(blob);

    let pom = document.createElement('a');
    pom.href = url;
    pom.setAttribute('download', fileName);

    pom.click();
  }
}
